// Device fingerprinting utility for session management
// Uses Web Crypto API for secure hashing

export interface DeviceInfo {
  deviceName: string;
  deviceType: string;
  browserInfo: string;
  deviceFingerprint: string;
  locationInfo?: {
    timezone: string;
    language: string;
    screen: {
      width: number;
      height: number;
      colorDepth: number;
    };
    platform: string;
  };
}

/**
 * Generates a SHA-256 hash of the input string using Web Crypto API
 */
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates a canvas fingerprint component
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');

    if (!ctx) return '';

    // Draw various shapes and text with different styles
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.font = '11pt Arial';
    ctx.fillText('Cwm fjordbank', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.font = '18pt Arial';
    ctx.fillText('Cwm fjordbank', 4, 45);

    // Add gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    gradient.addColorStop(0, 'red');
    gradient.addColorStop(0.5, 'green');
    gradient.addColorStop(1, 'blue');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 30, 200, 10);

    return canvas.toDataURL();
  } catch {
    return '';
  }
}

/**
 * Gets WebGL renderer information
 */
function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) return '';

    const webglContext = gl as WebGLRenderingContext;
    const debugInfo = webglContext.getExtension('WEBGL_debug_renderer_info');

    if (debugInfo) {
      const vendor = webglContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      const renderer = webglContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      return `${vendor}~${renderer}`;
    }

    return '';
  } catch {
    return '';
  }
}

/**
 * Gets audio context fingerprint
 */
function getAudioFingerprint(): string {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const analyser = audioContext.createAnalyser();
    const gainNode = audioContext.createGain();
    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

    gainNode.gain.value = 0; // Mute
    oscillator.type = 'triangle';
    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(0);

    const fingerprint = `${audioContext.sampleRate}~${analyser.fftSize}~${audioContext.destination.maxChannelCount}`;

    oscillator.stop();
    audioContext.close();

    return fingerprint;
  } catch {
    return '';
  }
}

/**
 * Gets installed fonts fingerprint (limited set)
 */
function getFontsFingerprint(): string {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testFonts = [
    'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana',
    'Helvetica', 'Comic Sans MS', 'Impact', 'Lucida Console'
  ];

  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const body = document.getElementsByTagName('body')[0];
  const span = document.createElement('span');

  span.style.fontSize = testSize;
  span.innerHTML = testString;
  span.style.position = 'absolute';
  span.style.left = '-9999px';
  span.style.visibility = 'hidden';

  const baseFontWidths: Record<string, number> = {};

  // Get base font widths
  for (const baseFont of baseFonts) {
    span.style.fontFamily = baseFont;
    body.appendChild(span);
    baseFontWidths[baseFont] = span.offsetWidth;
    body.removeChild(span);
  }

  const detectedFonts: string[] = [];

  // Test each font
  for (const font of testFonts) {
    let detected = false;
    for (const baseFont of baseFonts) {
      span.style.fontFamily = `'${font}', ${baseFont}`;
      body.appendChild(span);
      const width = span.offsetWidth;
      body.removeChild(span);

      if (width !== baseFontWidths[baseFont]) {
        detected = true;
        break;
      }
    }
    if (detected) {
      detectedFonts.push(font);
    }
  }

  return detectedFonts.join(',');
}

/**
 * Generates a cryptographically secure device fingerprint
 */
export const generateDeviceFingerprint = async (): Promise<string> => {
  // Collect various browser/device characteristics
  const components: string[] = [
    // Browser information
    navigator.userAgent,
    navigator.language,
    navigator.languages?.join(',') || '',
    String(navigator.hardwareConcurrency || ''),
    String(navigator.maxTouchPoints || 0),

    // Screen information
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    `${screen.availWidth}x${screen.availHeight}`,
    String(window.devicePixelRatio || 1),

    // Timezone
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    String(new Date().getTimezoneOffset()),

    // Platform
    navigator.platform || '',

    // Features
    navigator.cookieEnabled ? '1' : '0',
    typeof navigator.doNotTrack !== 'undefined' ? String(navigator.doNotTrack) : '',

    // Storage
    typeof localStorage !== 'undefined' ? '1' : '0',
    typeof sessionStorage !== 'undefined' ? '1' : '0',
    typeof indexedDB !== 'undefined' ? '1' : '0',

    // Canvas fingerprint
    getCanvasFingerprint(),

    // WebGL fingerprint
    getWebGLFingerprint(),

    // Audio fingerprint
    getAudioFingerprint(),

    // Fonts fingerprint
    getFontsFingerprint(),
  ];

  const fingerprintData = components.join('|||');

  // Use SHA-256 for cryptographic hashing
  const hash = await sha256(fingerprintData);

  // Return first 32 characters for a reasonable length
  return hash.substring(0, 32);
};

/**
 * Synchronous fallback for when async is not possible
 * Uses a simpler but still reasonably unique fingerprint
 */
export const generateDeviceFingerprintSync = (): string => {
  const components = [
    navigator.userAgent,
    navigator.language,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.platform || '',
    String(navigator.hardwareConcurrency || ''),
    getCanvasFingerprint(),
    getWebGLFingerprint(),
  ];

  const fingerprintData = components.join('|||');

  // Simple but better hash function (djb2 variant)
  let hash = 5381;
  for (let i = 0; i < fingerprintData.length; i++) {
    hash = ((hash << 5) + hash) ^ fingerprintData.charCodeAt(i);
  }

  // Convert to base36 and ensure positive
  return Math.abs(hash).toString(36) + Date.now().toString(36).slice(-4);
};

export const getDeviceInfo = async (fingerprint?: string): Promise<DeviceInfo> => {
  const userAgent = navigator.userAgent;

  // Detect device type
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);

  let deviceType = 'desktop';
  if (isTablet) deviceType = 'tablet';
  else if (isMobile) deviceType = 'mobile';

  // Extract device/browser info
  let deviceName = 'Unknown Device';
  if (userAgent.includes('Edg/')) deviceName = 'Edge Browser';
  else if (userAgent.includes('Chrome')) deviceName = 'Chrome Browser';
  else if (userAgent.includes('Firefox')) deviceName = 'Firefox Browser';
  else if (userAgent.includes('Safari')) deviceName = 'Safari Browser';

  // Add device model for mobile
  if (isMobile) {
    if (userAgent.includes('iPhone')) deviceName = 'iPhone';
    else if (userAgent.includes('iPad')) deviceName = 'iPad';
    else if (userAgent.includes('Android')) {
      // Try to extract Android device model
      const match = userAgent.match(/Android[^;]*;\s*([^)]+)\)/);
      deviceName = match ? `Android - ${match[1].split(' Build')[0].trim()}` : 'Android Device';
    }
  }

  // Generate fingerprint if not provided
  const deviceFingerprint = fingerprint || await generateDeviceFingerprint();

  return {
    deviceName,
    deviceType,
    browserInfo: userAgent,
    deviceFingerprint,
    locationInfo: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
      },
      platform: navigator.platform || 'unknown',
    },
  };
};

/**
 * Synchronous version for contexts where async is not possible
 */
export const getDeviceInfoSync = (fingerprint?: string): DeviceInfo => {
  const userAgent = navigator.userAgent;

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);

  let deviceType = 'desktop';
  if (isTablet) deviceType = 'tablet';
  else if (isMobile) deviceType = 'mobile';

  let deviceName = 'Unknown Device';
  if (userAgent.includes('Edg/')) deviceName = 'Edge Browser';
  else if (userAgent.includes('Chrome')) deviceName = 'Chrome Browser';
  else if (userAgent.includes('Firefox')) deviceName = 'Firefox Browser';
  else if (userAgent.includes('Safari')) deviceName = 'Safari Browser';

  if (isMobile) {
    if (userAgent.includes('iPhone')) deviceName = 'iPhone';
    else if (userAgent.includes('iPad')) deviceName = 'iPad';
    else if (userAgent.includes('Android')) {
      const match = userAgent.match(/Android[^;]*;\s*([^)]+)\)/);
      deviceName = match ? `Android - ${match[1].split(' Build')[0].trim()}` : 'Android Device';
    }
  }

  return {
    deviceName,
    deviceType,
    browserInfo: userAgent,
    deviceFingerprint: fingerprint || generateDeviceFingerprintSync(),
    locationInfo: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
      },
      platform: navigator.platform || 'unknown',
    },
  };
};
